import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { Cohort, Paged } from '@/types/cohort-api';

/**
 * Cohort reads for the enrolment flow (PROD-3206). Key shape mirrors pm-react #1242 so the two
 * apps invalidate the same way. Only called while `isCohortEnrolmentOn()` is on: the
 * hosting dialog mounts only behind the flag.
 */

export const COHORTS_KEY = ['cohorts'] as const;
export const cohortsListKey = () => [...COHORTS_KEY, 'list'] as const;
export const cohortPatientMembershipsPrefix = (patientId: string) =>
  [...COHORTS_KEY, 'patient-memberships', patientId] as const;

/** C1 pins paged lists as `{ items, total, page, pageSize }`; accept a bare array too. */
export function cohortItems(data: Cohort[] | Paged<Cohort> | null | undefined): Cohort[] {
  if (!data) return [];
  const list = Array.isArray(data) ? data : (data.items ?? []);
  return list.filter((c) => !c.isDeleted);
}

/** The org's live cohorts (deleted rows dropped), for the enrol picker. */
export function useCohorts({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: cohortsListKey(),
    enabled,
    queryFn: ({ signal }) => get<Cohort[] | Paged<Cohort>>('/api/Cohorts', { signal }),
    select: cohortItems,
  });
}
